import React from 'react';
import { color, font, radius, shadow, space, text, weight } from '../theme';

/* ── Card: the white surface every block of content sits on ─────────────── */

interface CardProps {
  children: React.ReactNode;
  // Inner padding in px; 0 for cards whose rows run edge to edge (ListRow)
  padding?: number;
  style?: React.CSSProperties;
}

export const Card = ({ children, padding = space.lg, style }: CardProps) => (
  <div style={{
    background: color.surface,
    borderRadius: radius.xl,
    boxShadow: shadow.card,
    padding,
    overflow: padding === 0 ? 'hidden' : undefined,
    ...style,
  }}>
    {children}
  </div>
);

/* ── Heading row: title, optional count/meta, optional action on the far side ── */

interface HeadingRowProps {
  title: React.ReactNode;
  meta?: React.ReactNode;
  action?: React.ReactNode;
  style?: React.CSSProperties;
}

const HeadingRow = ({ title, meta, action, style }: HeadingRowProps) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: space.sm, minHeight: 24, ...style }}>
    <h2 style={text.heading}>{title}</h2>
    {meta !== undefined && <span style={{ fontSize: font.small, color: color.textSecondary }}>{meta}</span>}
    {action && <div style={{ marginInlineStart: 'auto', fontSize: font.label, fontWeight: weight.medium }}>{action}</div>}
  </div>
);

// A heading inside a card, above its content
export const CardHeader = (props: HeadingRowProps) => <HeadingRow {...props} style={{ marginBottom: space.md, ...props.style }} />;

/* ── Section: a heading on the page background with its content below ────── */

interface SectionProps extends HeadingRowProps {
  children: React.ReactNode;
  gap?: number;
}

export const Section = ({ children, gap = space.sm, style, ...heading }: SectionProps) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap, ...style }}>
    <HeadingRow {...heading} />
    {children}
  </section>
);

export default Card;
